import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { authHeaders } from './gradeUtils';

const ResultRow = ({ r, onMark, busy }) => (
  <div className="flex items-start gap-2 py-2 border-b border-zinc-900" data-testid={`ws-review-row-${r.id}`}>
    {r.correct ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />}
    <div className="flex-1 min-w-0">
      <div className="text-xs text-zinc-300">{r.prompt || r.id}</div>
      <div className="text-[11px] mt-0.5">
        <span className="text-zinc-500">Wrote: </span><span className="text-white font-bold">{String(r.given || '—')}</span>
        <span className="text-zinc-500"> · Key: </span><span className="text-zinc-300">{String(r.answer || '')}</span>
        {r.teacher_override && <span className="text-emerald-400 font-black uppercase tracking-widest text-[9px] ml-2">Teacher override</span>}
      </div>
    </div>
    {!r.correct && (
      <button onClick={() => onMark(r.id)} disabled={busy} className="shrink-0 border-2 border-emerald-500/60 hover:bg-emerald-500 hover:text-black text-emerald-400 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest disabled:opacity-50" data-testid={`ws-mark-correct-${r.id}`}>
        Mark Correct
      </button>
    )}
  </div>
);

export const WorksheetReview = ({ studentId }) => {
  const [subs, setSubs] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/school/admin/worksheet/submissions?student_id=${studentId}`, { headers: authHeaders() })
      .then((r) => { setSubs(r.data.submissions); setOpen((o) => o || r.data.submissions[0]?.date || null); })
      .catch(() => setSubs([]));
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const mark = async (date, qid) => {
    setBusy(true);
    try {
      const r = await api.post('/school/admin/worksheet/regrade', { student_id: studentId, date, mark_correct: [qid] }, { headers: authHeaders() });
      toast.success(`Marked correct · score now ${r.data.submission.objective_score}%`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
    finally { setBusy(false); }
  };

  if (!subs) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;
  return (
    <div className="border-2 border-zinc-800 p-4 mb-6" data-testid="worksheet-review">
      <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="w-4 h-4 text-orange-400" /><span className="text-xs font-black uppercase tracking-widest">Daily Worksheet Review</span><span className="text-[10px] text-zinc-500">· fix any wrong grade</span></div>
      {subs.length === 0 ? <div className="text-xs text-zinc-500" data-testid="worksheet-review-empty">No worksheets submitted yet.</div> : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {subs.map((s) => (
              <button key={s.date} onClick={() => setOpen(s.date)} className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest border-2 ${open === s.date ? 'bg-orange-500 border-orange-500 text-black' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={`ws-review-date-${s.date}`}>
                {s.date.slice(5)} · {s.objective_score}%
              </button>
            ))}
          </div>
          {subs.filter((s) => s.date === open).map((s) => (
            <div key={s.date}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{s.subject} · {s.title}</div>
              {(s.results || []).filter((r) => r.type !== 'problem').map((r) => <ResultRow key={r.id} r={r} busy={busy} onMark={(qid) => mark(s.date, qid)} />)}
            </div>
          ))}
        </>
      )}
    </div>
  );
};
